import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, BarChart as BarChartIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import LoadingQuip from '@/components/LoadingQuip';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminCourseSurveyResults() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const courseId = searchParams.get('courseId');
  const courseTitle = searchParams.get('courseTitle') || 'Course';
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [responses, setResponses] = useState([]);

  useEffect(() => {
    if (!courseId) {
      toast.error('Course ID is required');
      navigate('/admin');
      return;
    }

    fetchSurveyData();
  }, [courseId]);

  const fetchSurveyData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/api/admin/course-survey-stats/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch survey data');
      }

      const data = await response.json();
      setStats(data);
      setResponses(data.responses || []);
    } catch (error) {
      console.error('Error fetching survey data:', error);
      toast.error('Error loading survey results');
    } finally {
      setLoading(false);
    }
  };

  const deleteSurveyResponse = async (responseId) => {
    if (!confirm('Are you sure you want to delete this survey response? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/api/admin/course-survey-response/${responseId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete survey response');
      }

      toast.success('Survey response deleted successfully');
      
      // Remove from local state
      setResponses(responses.filter(r => r.id !== responseId));
      
      // Update stats
      if (stats) {
        setStats({
          ...stats,
          count: stats.count - 1
        });
      }
    } catch (error) {
      console.error('Error deleting survey response:', error);
      toast.error('Error deleting survey response');
    }
  };

  const exportCSV = () => {
    if (responses.length === 0) {
      toast.error('No responses to export');
      return;
    }

    // Create CSV header
    const headers = [
      'Submitted At',
      'Email',
      'Profession',
      'Years Practicing',
      'Practice Type',
      'Q5: Course Objectives',
      'Q6: Instructor Knowledge',
      'Q7: Material Quality',
      'Q8: Relevance',
      'Q9: Understanding',
      'Q10: Presentation',
      'Content Level',
      'Preferred Day',
      'Preferred Time',
      'Learning Format',
      'Networking Interest',
      'Cost Factor',
      'Team Training',
      'Most Valuable',
      'Suggested Changes',
      'Other Feedback'
    ];

    const csvRows = [
      headers.join(','),
      ...responses.map(r => [
        new Date(r.submitted_at).toLocaleDateString(),
        `"${r.q1_email}"`,
        `"${r.q2_profession}"`,
        `"${r.q3_years_practicing}"`,
        `"${r.q4_practice_type}"`,
        r.q5_course_objectives,
        r.q6_instructor_knowledge,
        r.q7_material_quality,
        r.q8_relevance,
        r.q9_understanding_improvement,
        r.q10_presentation_effectiveness,
        `"${r.q12_content_level}"`,
        `"${r.q13_preferred_day}"`,
        `"${r.q14_preferred_time}"`,
        `"${r.q15_learning_format}"`,
        r.q16_networking_interest,
        r.q17_cost_factor,
        r.q18_team_training_interest,
        `"${(r.q19_most_valuable_aspect || '').replace(/"/g, '""')}"`,
        `"${(r.q20_suggested_changes || '').replace(/"/g, '""')}"`,
        `"${(r.q21_other_feedback || '').replace(/"/g, '""')}"`
      ].join(','))
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `course-survey-${courseId}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F4F4F4 0%, #DADEEO 100%)' }}>
        <LoadingQuip size="lg" />
      </div>
    );
  }

  const likertLabels = {
    q5_course_objectives: 'Course Objectives',
    q6_instructor_knowledge: 'Instructor Knowledge',
    q7_material_quality: 'Material Quality',
    q8_relevance: 'Relevance to Practice',
    q9_understanding_improvement: 'Understanding Improvement',
    q10_presentation_effectiveness: 'Presentation Effectiveness'
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #F4F4F4 0%, #DADEEO 100%)' }}>
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/admin')}
            variant="outline"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Dashboard
          </Button>
          
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ color: '#092036' }}>
                Course Survey Results
              </h1>
              <p className="text-lg" style={{ color: '#102862' }}>
                {courseTitle}
              </p>
            </div>
            
            {responses.length > 0 && (
              <Button
                onClick={exportCSV}
                className="gap-2 text-white"
                style={{ background: '#102862' }}
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            )}
          </div>
        </div>

        {stats?.count === 0 ? (
          <Card className="p-12 text-center">
            <BarChartIcon className="w-16 h-16 mx-auto mb-4" style={{ color: '#DADEEO' }} />
            <h3 className="text-xl font-semibold mb-2" style={{ color: '#70767C' }}>
              No survey responses yet
            </h3>
            <p style={{ color: '#70767C' }}>
              Students haven't submitted any surveys for this course yet.
            </p>
          </Card>
        ) : (
          <>
            {/* Summary Statistics */}
            <Card className="mb-6 p-6" style={{ background: '#f0fdf4', border: '2px solid #16a34a' }}>
              <h2 className="text-xl font-bold mb-4" style={{ color: '#092036' }}>
                Summary Statistics
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm" style={{ color: '#70767C' }}>Total Responses</p>
                  <p className="text-3xl font-bold" style={{ color: '#16a34a' }}>{stats?.count}</p>
                </div>
                
                {stats?.averages && Object.entries(likertLabels).map(([key, label]) => (
                  <div key={key}>
                    <p className="text-sm" style={{ color: '#70767C' }}>{label}</p>
                    <div className="flex items-end gap-2">
                      <p className="text-2xl font-bold" style={{ color: '#102862' }}>
                        {stats.averages[key]?.toFixed(1) || 'N/A'}
                      </p>
                      <p className="text-sm mb-1" style={{ color: '#70767C' }}>/ 5.0</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Individual Responses */}
            <Card className="p-6">
              <h2 className="text-xl font-bold mb-4" style={{ color: '#092036' }}>
                Individual Responses ({responses.length})
              </h2>
              
              <div className="space-y-6">
                {responses.map((response, index) => (
                  <Card key={response.id} className="p-6" style={{ background: '#f9fafb' }}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="font-semibold" style={{ color: '#092036' }}>
                          Response #{responses.length - index}
                        </p>
                        <p className="text-sm" style={{ color: '#70767C' }}>
                          {new Date(response.submitted_at).toLocaleDateString('en-US', { 
                            month: 'long', 
                            day: 'numeric', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-sm" style={{ color: '#70767C' }}>
                          {response.q1_email}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteSurveyResponse(response.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Demographics */}
                      <div>
                        <h4 className="font-semibold mb-2 text-sm" style={{ color: '#092036' }}>Demographics</h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="font-medium">Profession:</span> {response.q2_profession}</p>
                          {response.q2_profession_other && (
                            <p className="pl-4 text-xs" style={{ color: '#70767C' }}>({response.q2_profession_other})</p>
                          )}
                          <p><span className="font-medium">Years:</span> {response.q3_years_practicing}</p>
                          <p><span className="font-medium">Practice:</span> {response.q4_practice_type}</p>
                        </div>
                      </div>

                      {/* Ratings */}
                      <div>
                        <h4 className="font-semibold mb-2 text-sm" style={{ color: '#092036' }}>Course Ratings</h4>
                        <div className="space-y-1 text-sm">
                          {Object.entries(likertLabels).map(([key, label]) => (
                            <div key={key} className="flex justify-between">
                              <span>{label}:</span>
                              <span className="font-semibold">{response[key]}/5</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Preferences */}
                      <div>
                        <h4 className="font-semibold mb-2 text-sm" style={{ color: '#092036' }}>Preferences</h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="font-medium">Level:</span> {response.q12_content_level}</p>
                          <p><span className="font-medium">Day:</span> {response.q13_preferred_day}</p>
                          <p><span className="font-medium">Time:</span> {response.q14_preferred_time}</p>
                          <p><span className="font-medium">Format:</span> {response.q15_learning_format}</p>
                        </div>
                      </div>

                      {/* Interests */}
                      <div>
                        <h4 className="font-semibold mb-2 text-sm" style={{ color: '#092036' }}>Additional Info</h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="font-medium">Networking:</span> {response.q16_networking_interest}</p>
                          <p><span className="font-medium">Cost factor:</span> {response.q17_cost_factor}</p>
                          <p><span className="font-medium">Team training:</span> {response.q18_team_training_interest}</p>
                        </div>
                      </div>
                    </div>

                    {/* Open Feedback */}
                    {(response.q19_most_valuable_aspect || response.q20_suggested_changes || response.q21_other_feedback) && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="font-semibold mb-2 text-sm" style={{ color: '#092036' }}>Open Feedback</h4>
                        {response.q19_most_valuable_aspect && (
                          <div className="mb-2">
                            <p className="text-xs font-medium" style={{ color: '#70767C' }}>Most Valuable:</p>
                            <p className="text-sm">{response.q19_most_valuable_aspect}</p>
                          </div>
                        )}
                        {response.q20_suggested_changes && (
                          <div className="mb-2">
                            <p className="text-xs font-medium" style={{ color: '#70767C' }}>Suggested Changes:</p>
                            <p className="text-sm">{response.q20_suggested_changes}</p>
                          </div>
                        )}
                        {response.q21_other_feedback && (
                          <div>
                            <p className="text-xs font-medium" style={{ color: '#70767C' }}>Other Feedback:</p>
                            <p className="text-sm">{response.q21_other_feedback}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
